'use client';

import { useState, useRef, useCallback } from 'react';
import {
  RealtimeAgent,
  RealtimeSession,
  OpenAIRealtimeWebRTC,
  type RealtimeSessionConfig,
} from '@openai/agents/realtime';
import { tool } from '@openai/agents';
import { z } from 'zod';
import type { OrbState } from '@/components/interview/Orb';
import { buildRealtimeSystemPrompt } from '@/lib/mockStudy';

// ── Types ─────────────────────────────────────────────────────────────────────

export type ConnectionStatus = 'idle' | 'connecting' | 'ready' | 'error';
export type InterviewPhase = 'opening' | 'interviewing' | 'closing';

export interface TranscriptEntry {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  /**
   * Primary event time persisted to recordings.
   * For user speech this is when speech started; for assistant turns it is when
   * transcript finalisation arrives.
   */
  timestamp?: string;
  /** ISO timestamp when user speech started (if available). */
  spokenAt?: string;
  /** ISO timestamp when transcription completed (if available). */
  transcribedAt?: string;
}

export interface UseRealtimeSessionResult {
  status: ConnectionStatus;
  orbState: OrbState;
  /** 0-1 real microphone amplitude for orb animation. */
  micLevel: number;
  /** 0-1 real AI speaker amplitude for orb animation. */
  aiLevel: number;
  /** True once the AI has called the end_interview tool. */
  ended: boolean;
  /** opening → interviewing (first user speech) → closing (end_interview detected). */
  phase: InterviewPhase;
  error: string | null;
  transcript: TranscriptEntry[];
  /** Streams in as audio_transcript.delta events arrive. */
  currentAiText: string;
  /** Live streaming text during AI speech — clears when the turn ends. For debug/test UIs. */
  streamingAiText: string;
  /** Changes each AI turn to trigger fade transitions. */
  aiTurnId: string;
  start: (
    voice?: string,
    existingStream?: MediaStream,
    noiseReductionType?: 'near_field' | 'far_field',
    outputDeviceId?: string,
  ) => Promise<void>;
  stop: () => void;
  /** Send a text message (test interface). */
  sendText: (text: string) => void;
  /** Ask the AI to repeat its last question. */
  repeat: () => void;
  /** Enable or disable the outgoing mic track. */
  mute: (shouldMute: boolean) => void;
  /** Pause/resume server-side VAD. */
  pause: (shouldPause: boolean) => void;
}

// ── VAD configs ───────────────────────────────────────────────────────────────

// create_response is intentionally false: with interrupt_response:false the server
// cannot cancel an ongoing AI response, so if it also auto-creates on speech_stop it
// will collide and return "active response in progress". We send response.create
// manually from speech_stopped when the AI is not currently responding.
const OPENING_VAD_CONFIG = {
  type: 'semantic_vad',
  interrupt_response: false,
  create_response: false,
  eagerness: 'high',
} as const;

const INTERVIEWING_VAD_CONFIG = {
  type: 'semantic_vad',
  interrupt_response: false,
  create_response: false,
  eagerness: 'medium',
} as const;

// Real-time speech-rate guardrail. The mic is MUTED while the AI is speaking (so the
// server never hears the user and can't be disrupted). To capture a quick reply the
// instant the AI finishes, we re-open the mic at the true end — but NOT during
// mid-message pauses (which would let the server hear the user → it ducks/truncates
// the AI). We tell "true end" from "pause" by measuring the AI's actual speaking time
// frame-by-frame (frames where avg > 8) vs the total transcript length: the guardrail
// passes only once speakingMs/totalChars reaches MIN_SPEAKING_RATE_MS_PER_CHAR, which
// can't happen until the AI has physically said its whole message.
// Tuning: the HIGHER this is, the LATER — i.e. closer to the true end — the guardrail
// releases, so a late mid-message pause is less likely to re-open the mic mid-message.
// BUT it must stay strictly BELOW the FASTEST real response's pace (lowest ms/char),
// else the ratio never reaches it and the mic only re-opens at confirmAiDone (~1.7 s
// late) → a quick reply is MISSED. Pace varies a lot run-to-run: observed ~48.5–58
// ms/char for the SAME text, so leave real margin below the low end. 50 was too high
// (a 48.5 run never released); 44 keeps ~4.5 margin. If a quick reply is still missed,
// drop toward 40; if the AI gets disrupted by a late pause, raise it (but stay <48).
const MIN_SPEAKING_RATE_MS_PER_CHAR = 44;
// The AI is "confirmed done" — clears isAiRespondingRef, flips the orb to listening,
// and fires the deferred reply — after this many frames (~60 fps) of CONTINUOUS audio
// silence. It directly sets the "thinking" latency between the user replying and the
// AI answering, so lower = snappier. BUT it must stay LONGER than the AI's longest
// natural PAUSE, else a mid-message pause is mistaken for the end and the AI's final
// clause gets cut (or the orb flips early). The `[poller] pause ended — was Xs` logs
// show the actual longest pause this session — keep this comfortably above it. (The
// muted design plays the AI uninterrupted, so pauses are shorter than the ~1.5 s seen
// in the abandoned always-open mode, which the server duck had inflated.)
const CONFIRM_AI_DONE_FRAMES = 66; // ~1.1 s — longest observed AI pause was 0.90 s; ~0.2 s margin

// Grace period after speech_stopped before sending response.create.
const RESPONSE_GRACE_MS = 750;

// ── Agent (module-level singleton — instructions are static) ──────────────────

// The AI calls this tool after its closing message to signal the interview is
// done. The SDK automatically handles the function_call_output response.
const endInterviewTool = tool({
  name: 'end_interview',
  description:
    'Signal that the interview has concluded. Call this immediately after ' +
    'delivering the closing message — no further speech should follow.',
  parameters: z.object({}),
  execute: async () => '{"acknowledged":true}',
});

const interviewAgent = new RealtimeAgent({
  name: 'AI Interviewer',
  instructions: buildRealtimeSystemPrompt(),
  tools: [endInterviewTool],
});

// ── Recording helpers (module-level) ──────────────────────────────────────────

// Streams audio chunks out every 5 s so data is persisted as the interview
// progresses (rather than only at the end).
function createStreamingRecorder(
  stream: MediaStream,
  onChunk: (blob: Blob) => void,
): MediaRecorder {
  const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
    ? 'audio/webm;codecs=opus'
    : '';
  const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
  recorder.ondataavailable = (e) => { if (e.data.size > 0) onChunk(e.data); };
  recorder.start(5000);
  return recorder;
}

function stopRecorder(recorder: MediaRecorder | null) {
  if (recorder && recorder.state !== 'inactive') recorder.stop();
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useRealtimeSession(): UseRealtimeSessionResult {
  const [status, setStatus] = useState<ConnectionStatus>('idle');
  const [orbState, setOrbState] = useState<OrbState>('idle');
  const [micLevel, setMicLevel] = useState(0);
  const [aiLevel, setAiLevel] = useState(0);
  const [ended, setEnded] = useState(false);
  const [phase, setPhase] = useState<InterviewPhase>('opening');
  const [error, setError] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [currentAiText, setCurrentAiText] = useState('');
  const [streamingAiText, setStreamingAiText] = useState('');
  const [aiTurnId, setAiTurnId] = useState('');

  const sessionRef = useRef<RealtimeSession | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const micCtxRef = useRef<AudioContext | null>(null);
  const mouseDownHandlerRef = useRef<(() => void) | null>(null);
  const rafRef = useRef<number>(0);
  const remoteAnalyserRef = useRef<AnalyserNode | null>(null);
  const remoteAudioCtxRef = useRef<AudioContext | null>(null);
  const aiLevelRafRef = useRef<number>(0);
  const silenceRafRef = useRef<number>(0);
  const partialRef = useRef<Record<string, string>>({});
  const userMutedRef = useRef(false);
  const micRenableTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // True from response.created until silence is confirmed — guards speech events
  // from flipping orb state while AI is still talking.
  const isAiRespondingRef = useRef(false);
  // True only while the API response is still active (response.created →
  // response.done/cancelled). Safe to send response.cancel only when true.
  const isResponseActiveRef = useRef(false);
  const speechTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const phaseRef = useRef<InterviewPhase>('opening');
  // Placeholder IDs inserted on speech_started, in start order.
  const pendingUserPlaceholderQueueRef = useRef<string[]>([]);
  // Maps a committed user item_id to its placeholder ID so replacement is stable
  // even when multiple user turns are in flight.
  const pendingUserPlaceholderByItemIdRef = useRef<Record<string, string>>({});
  // speech_started timestamps in start order, used when no placeholder exists.
  const pendingUserSpeechStartTimesRef = useRef<string[]>([]);
  // AI transcript entries accumulated during a response turn. Flushed to state
  // only after silence is detected so text appears after speech, not during.
  const pendingAiEntriesRef = useRef<TranscriptEntry[]>([]);
  // Total characters of the current AI response's transcript, accumulated as deltas
  // stream in. Drives the real-time speech-rate guardrail in the silence poller.
  const aiResponseCharsRef = useRef(0);
  // Tracks whether the user is currently mid-utterance (speech_started without a
  // matching speech_stopped), regardless of phase.
  const userSpeakingRef = useRef(false);

  // True when the in-flight AI response began during the opening phase. The opening
  // message must never appear as a caption — but by the time it's flushed the phase
  // has often already flipped to 'interviewing' (the user's reply transitioned it),
  // so we can't rely on phase at flush time. Captured at response.created instead.
  const currentResponseIsOpeningRef = useRef(true);
  // Set to true by the FIRST confirmAiDone for each response turn; cleared by
  // response.created. Guards against zombie/ghost pollers (created when response.done
  // arrives after confirmAiDone has already set silenceRafRef=0, re-arming the poller)
  // calling confirmAiDone a second time and firing an extra response.create.
  const aiTurnConfirmedDoneRef = useRef(false);
  // Recording — audio is streamed to /api/save-recording, transcript persisted alongside.
  const sessionIdRef = useRef<string | null>(null);
  const userRecorderRef = useRef<MediaRecorder | null>(null);
  const aiRecorderRef = useRef<MediaRecorder | null>(null);
  // Mirror of transcript state — updated in sync with every setTranscript call so
  // persistTranscript() can read the latest value without waiting for a re-render.
  const transcriptRef = useRef<TranscriptEntry[]>([]);

  // ── Mic helpers ─────────────────────────────────────────────────────────────

  const setMicEnabled = useCallback((enabled: boolean) => {
    micStreamRef.current
      ?.getTracks()
      .filter((t) => t.kind === 'audio')
      .forEach((t) => { t.enabled = enabled; });
  }, []);

  const stopResources = useCallback(() => {
    if (mouseDownHandlerRef.current) {
      document.removeEventListener('mousedown', mouseDownHandlerRef.current);
      mouseDownHandlerRef.current = null;
    }
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (aiLevelRafRef.current) { cancelAnimationFrame(aiLevelRafRef.current); aiLevelRafRef.current = 0; }
    if (silenceRafRef.current) { cancelAnimationFrame(silenceRafRef.current); silenceRafRef.current = 0; }
    if (micRenableTimerRef.current) {
      clearTimeout(micRenableTimerRef.current);
      micRenableTimerRef.current = null;
    }
    if (speechTimerRef.current) {
      clearTimeout(speechTimerRef.current);
      speechTimerRef.current = null;
    }
    micStreamRef.current?.getTracks().forEach((t) => t.stop());
    micStreamRef.current = null;
    micCtxRef.current?.close().catch(() => {});
    micCtxRef.current = null;
    remoteAudioCtxRef.current?.close().catch(() => {});
    remoteAudioCtxRef.current = null;
    remoteAnalyserRef.current = null;
    userMutedRef.current = false;
    setMicLevel(0);
    setAiLevel(0);
  }, []);

  const sendAudioChunk = useCallback((field: 'userAudioChunk' | 'aiAudioChunk', blob: Blob) => {
    if (!sessionIdRef.current) return;
    const fd = new FormData();
    fd.append('sessionId', sessionIdRef.current);
    fd.append(field, blob, 'chunk.webm');
    fetch('/api/save-recording', { method: 'POST', body: fd }).catch(console.error);
  }, []);

  const persistTranscript = useCallback(() => {
    if (!sessionIdRef.current || !transcriptRef.current.length) return;
    const fd = new FormData();
    fd.append('sessionId', sessionIdRef.current);
    fd.append('transcript', JSON.stringify(transcriptRef.current));
    fetch('/api/save-recording', { method: 'POST', body: fd }).catch(console.error);
  }, []);

  const flushPendingAiEntries = useCallback(() => {
    const pending = pendingAiEntriesRef.current;
    if (pending.length === 0) return;
    // The opening message must never surface as a caption (the phase may already
    // have flipped to 'interviewing' by now). Still record it to the transcript
    // below for recordings — only the on-screen caption is suppressed.
    if (!currentResponseIsOpeningRef.current) setCurrentAiText(pending[pending.length - 1].text);
    // Compute from the ref synchronously so transcriptRef is up-to-date before
    // persistTranscript() runs. The setTranscript functional updater runs later
    // (React batching), so it can't be relied on to update the ref in time.
    let next = [...transcriptRef.current];
    // Remove any individually-added entries for the pending items, then append all
    // pending entries in order so natural pauses/splits remain visible in transcript.
    for (const entry of pending) {
      const idx = next.findIndex((e) => e.id === entry.id && e.role === 'assistant');
      if (idx >= 0) next.splice(idx, 1);
    }
    next = [...next, ...pending];
    transcriptRef.current = next;
    setTranscript(next);
    pendingAiEntriesRef.current = [];
    setStreamingAiText('');
    persistTranscript();
  }, [persistTranscript]);

  // ── Silence poller ────────────────────────────────────────────────────────────
  // Must be armed at response.created (not response.output_audio.done) so that
  // seenAudio is set while the AI is actively speaking. response.output_audio.done
  // fires when the SERVER finishes sending bytes — for streamed TTS this can be
  // seconds before the client finishes playing, so using it as the start trigger
  // means seenAudio is never true and the poller falls through to PRE_AUDIO_FALLBACK.
  //
  // The mic is MUTED while the AI speaks; this poller watches the AI's OUTPUT audio.
  // State machine (per frame):
  //   avg > 8  → seenAudio=true, speakingFrames++, reset silentFrames (+ re-mute if reopened)
  //   seenAudio + guardrail passed + silence ≥ MIC_REOPEN_THRESHOLD → re-open mic for CAPTURE
  //   seenAudio + silence ≥ CONFIRM_AI_DONE_FRAMES → AI confirmed done → clear
  //     isAiRespondingRef + (deferred) respond if a reply was captured
  //   !seenAudio + preFrames > PRE_AUDIO_FALLBACK → confirm done (non-audio
  //     responses, e.g. function-call-only turns, or a genuinely broken analyser)
  const armSilencePoller = useCallback(() => {
    if (silenceRafRef.current || micRenableTimerRef.current) return;
    const t0 = performance.now();
    // The AI is confirmed done. Flush and go to listening — the normal
    // speech_stopped → grace timer path handles the next response.
    const confirmAiDone = () => {
      silenceRafRef.current = 0;
      // Idempotency guard: if a ghost/zombie poller (created when response.done arrives
      // after the legitimate confirmAiDone has already set silenceRafRef=0 and re-armed
      // armSilencePoller) reaches this point, bail out before touching any shared state.
      if (aiTurnConfirmedDoneRef.current) return;
      aiTurnConfirmedDoneRef.current = true;
      isAiRespondingRef.current = false;
      console.log(`[poller] AI confirmed done  silentFrames=${silentFrames}  userSpeaking=${userSpeakingRef.current}  elapsed=${((performance.now()-t0)/1000).toFixed(2)}s`);
      flushPendingAiEntries();
      if (!userMutedRef.current) setMicEnabled(true);
      setOrbState('listening');
    };
    let seenAudio = false;
    let silentFrames = 0;
    let preAudioFrames = 0;
    let micReopened = false;
    // Cumulative frames where avg > 8 = the AI's actual speaking time (NOT reset on
    // pause). Drives the guardrail: speakingFrames × 16.67ms / totalChars.
    let speakingFrames = 0;
    const MIC_REOPEN_THRESHOLD = 4; // ~65 ms of silence → re-open mic for capture
    const PRE_AUDIO_FALLBACK = 600;
    // The mic is muted while the AI speaks. The poller watches the AI's OUTPUT audio:
    // it re-opens the mic for CAPTURE only once the speech-rate guardrail says the AI
    // has physically finished its message (not just paused), re-mutes if the AI
    // resumes, and fires confirmAiDone after a long continuous silence.
    const checkSilence = () => {
      const ctx = remoteAudioCtxRef.current;
      if (ctx?.state === 'suspended') ctx.resume().catch(() => {});
      const analyser = remoteAnalyserRef.current;
      if (!analyser) {
        silenceRafRef.current = requestAnimationFrame(checkSilence);
        return;
      }
      const buf = new Uint8Array(analyser.frequencyBinCount);
      analyser.getByteFrequencyData(buf);
      const avg = buf.reduce((s, v) => s + v, 0) / buf.length;
      if (avg > 8) {
        // Any detected remote AI audio means we should be in speaking state.
        // This recovers quickly if status briefly flipped to listening between
        // segments while the same response is still continuing.
        setOrbState((prev) => (prev === 'speaking' ? prev : 'speaking'));
        // A pause just ENDED (AI resumed). Its length is a lower bound on CONFIRM_AI_
        // DONE_FRAMES — the longest such pause this session is the floor below which
        // lowering CONFIRM would start cutting off the AI's final clause.
        if (seenAudio && silentFrames >= 20) {
          console.log(`[poller] pause ended — was ${silentFrames} frames (${(silentFrames / 60).toFixed(2)}s) → CONFIRM_AI_DONE_FRAMES must exceed this`);
        }
        // AI audio resumed after we re-opened the mic early — that silence was just a
        // pause, not the end. Re-mute so the user can't be heard for the rest of the
        // message; the mic re-opens at the next gap and stays open only at the true end.
        if (micReopened) {
          micReopened = false;
          isAiRespondingRef.current = true;
          if (!userMutedRef.current) setMicEnabled(false);
          setOrbState('speaking');
        }
        seenAudio = true;
        speakingFrames++;
        silentFrames = 0;
      } else if (seenAudio) {
        silentFrames++;
        // Guardrail: only re-open the mic once the AI has physically spoken its whole
        // message (speakingMs/totalChars ≥ rate). During a mid-message pause speakingMs
        // hasn't caught up → stays shut → the server never hears the user mid-message.
        const speakingMs = speakingFrames * (1000 / 60);
        const totalChars = aiResponseCharsRef.current;
        const guardrailPassed = totalChars < 5 || (speakingMs / totalChars) >= MIN_SPEAKING_RATE_MS_PER_CHAR;
        if (silentFrames === 1 && !guardrailPassed) {
          console.log(`[poller] guardrail holding  chars=${totalChars}  speakingMs=${speakingMs.toFixed(0)}  rate=${(speakingMs / Math.max(totalChars, 1)).toFixed(1)}  need>=${MIN_SPEAKING_RATE_MS_PER_CHAR}`);
        }
        // Re-open the mic for CAPTURE (post-guardrail, short silence). Does NOT mark
        // the AI done — isAiRespondingRef stays true, so a reply here is recorded but
        // can't trigger a response. If it was a pause, the re-mute above shuts it again.
        if (guardrailPassed && !micReopened && silentFrames >= MIC_REOPEN_THRESHOLD && !isResponseActiveRef.current) {
          micReopened = true;
          console.log(`[poller] mic re-opened for capture  silentFrames=${silentFrames}  elapsed=${((performance.now()-t0)/1000).toFixed(2)}s`);
          if (!userMutedRef.current) setMicEnabled(true);
        }
        // AI confirmed done ONLY after continuous silence longer than any real pause.
        if (silentFrames >= CONFIRM_AI_DONE_FRAMES && !isResponseActiveRef.current) { confirmAiDone(); return; }
      } else {
        preAudioFrames++;
        if (preAudioFrames > PRE_AUDIO_FALLBACK && !isResponseActiveRef.current) { confirmAiDone(); return; }
      }
      silenceRafRef.current = requestAnimationFrame(checkSilence);
    };
    micRenableTimerRef.current = setTimeout(() => {
      micRenableTimerRef.current = null;
      silenceRafRef.current = requestAnimationFrame(checkSilence);
    }, 300);
  }, [setMicEnabled, flushPendingAiEntries]);

  // ── Raw event handler (wired to transport.on('*', ...)) ───────────────────

  const handleRawEvent = useCallback(
    (event: unknown) => {
      const msg = event as Record<string, unknown>;
      const type = msg.type as string;

      switch (type) {
        // AI is generating — reset per-turn state and start the silence poller.
        // Poller starts here (not at response.output_audio.done) so seenAudio is
        // guaranteed to be set while the AI is actually speaking.
        case 'response.created': {
          const id = (msg.response as Record<string, unknown> | null)?.id as string | undefined;
          setAiTurnId(id ?? String(Date.now()));
          setCurrentAiText('');
          setStreamingAiText('');
          setOrbState('thinking');
          isAiRespondingRef.current = true;
          isResponseActiveRef.current = true;
          aiResponseCharsRef.current = 0;
          aiTurnConfirmedDoneRef.current = false;
          currentResponseIsOpeningRef.current = phaseRef.current === 'opening';
          if (micRenableTimerRef.current) {
            clearTimeout(micRenableTimerRef.current);
            micRenableTimerRef.current = null;
          }
          if (speechTimerRef.current) {
            clearTimeout(speechTimerRef.current);
            speechTimerRef.current = null;
          }
          if (silenceRafRef.current) {
            cancelAnimationFrame(silenceRafRef.current);
            silenceRafRef.current = 0;
          }
          // Mute the mic while the AI speaks so the server never hears the user and
          // can't be disrupted (ducked/truncated). The poller re-opens it for capture
          // only at the true end of the message (post-guardrail).
          setMicEnabled(false);
          armSilencePoller();
          break;
        }

        // Streaming AI transcript — only accumulate; state is flushed after silence
        case 'response.output_audio_transcript.delta': {
          const itemId = msg.item_id as string;
          const delta = msg.delta as string;
          if (!delta) break;
          const accumulated = (partialRef.current[itemId] ?? '') + delta;
          partialRef.current[itemId] = accumulated;
          aiResponseCharsRef.current += delta.length;
          setStreamingAiText(accumulated);
          setOrbState('speaking');
          break;
        }

        // AI transcript finalised — park in pending; shown after silence detected
        case 'response.output_audio_transcript.done': {
          const itemId = msg.item_id as string;
          const text = msg.transcript as string;
          if (text) {
            pendingAiEntriesRef.current.push({ id: itemId, role: 'assistant', text, timestamp: new Date().toISOString() });
          }
          delete partialRef.current[itemId];
          break;
        }

        // response.output_audio.done fires when the server finishes sending all audio
        // bytes into the WebRTC stream (primary trigger). response.done is a fallback
        // in case the audio event doesn't arrive. Both arm the AnalyserNode poller —
        // armSilencePoller() is idempotent so whichever fires second is a no-op.
        case 'response.output_audio.done':
        case 'response.done': {
          isResponseActiveRef.current = false;
          armSilencePoller();
          break;
        }

        // AI response cancelled — cancel any running poller and do a short drain wait
        case 'response.cancelled':
          isResponseActiveRef.current = false;
          if (silenceRafRef.current) { cancelAnimationFrame(silenceRafRef.current); silenceRafRef.current = 0; }
          if (!micRenableTimerRef.current) {
            micRenableTimerRef.current = setTimeout(() => {
              micRenableTimerRef.current = null;
              isAiRespondingRef.current = false;
              flushPendingAiEntries();
              setOrbState('listening');
              if (!userMutedRef.current) setMicEnabled(true);
            }, 300);
          }
          break;

        // Guard against speech events firing while AI is responding (e.g. timing gaps
        // before mic mute takes effect). With interrupt_response:false the server won't
        // cancel the response, but we also don't want the orb state to flicker.
        case 'input_audio_buffer.speech_started':
          userSpeakingRef.current = true;
          if (speechTimerRef.current) {
            clearTimeout(speechTimerRef.current);
            speechTimerRef.current = null;
          }
          const speechStartedAt = new Date().toISOString();
          pendingUserSpeechStartTimesRef.current.push(speechStartedAt);
          if (!isAiRespondingRef.current) {
            // Insert a placeholder entry NOW so the user turn sits before the AI
            // response that will follow. The placeholder is replaced in-place when
            // the real transcription arrives (which is always later).
            const pendingId = `user-pending-${Date.now()}`;
            pendingUserPlaceholderQueueRef.current.push(pendingId);
            const nextWithPlaceholder: TranscriptEntry[] = [
              ...transcriptRef.current,
              { id: pendingId, role: 'user', text: '…', timestamp: speechStartedAt, spokenAt: speechStartedAt },
            ];
            transcriptRef.current = nextWithPlaceholder;
            setTranscript(nextWithPlaceholder);
            setOrbState('listening');
          }
          break;

        // Associates committed user item_id with the earliest placeholder captured
        // for this utterance so transcription.completed can replace the right entry.
        case 'input_audio_buffer.committed': {
          const itemId = msg.item_id as string | undefined;
          if (itemId && !pendingUserPlaceholderByItemIdRef.current[itemId]) {
            const placeholderId = pendingUserPlaceholderQueueRef.current.shift();
            if (placeholderId) pendingUserPlaceholderByItemIdRef.current[itemId] = placeholderId;
          }
          break;
        }

        case 'input_audio_buffer.speech_stopped':
          // Crucial: if the user speaks during an AI inter-sentence gap, the AI
          // resumes and the poller re-mutes (isAiRespondingRef → true) before this
          // timer fires, so the re-check below aborts and the opening is NOT interrupted.
          userSpeakingRef.current = false;
          // First user speech ends the opening phase. Do this here (not in
          // transcription.completed) so phaseRef is already 'interviewing' when
          // response.created fires and currentResponseIsOpeningRef is set correctly.
          if (phaseRef.current === 'opening') {
            phaseRef.current = 'interviewing';
            setPhase('interviewing');
            sessionRef.current?.transport.sendEvent({
              type: 'session.update',
              session: { type: 'realtime', audio: { input: { turn_detection: INTERVIEWING_VAD_CONFIG } } },
            });
          }
          // Utterance completed while the AI was still finishing — ignored; once the
          // AI is done the mic re-opens and normal VAD handles the next response.
          if (!isAiRespondingRef.current) {
            // Grace period: wait RESPONSE_GRACE_MS before triggering the AI response
            // in case the user resumes speaking. speech_started cancels this timer.
            speechTimerRef.current = setTimeout(() => {
              speechTimerRef.current = null;
              if (isAiRespondingRef.current) return;
              flushPendingAiEntries();
              setOrbState('thinking');
              sessionRef.current?.transport.sendEvent({ type: 'response.create' });
              isAiRespondingRef.current = true; // guard the gap until response.created
            }, RESPONSE_GRACE_MS);
          }
          break;

        // response.output_item.added fires as each output item starts — when we see
        // a function_call for end_interview it means the closing message has just
        // finished streaming. Clear the text immediately so it doesn't linger while
        // the function call completes.
        case 'response.output_item.added': {
          const item = msg.item as Record<string, unknown> | null;
          if (item?.type === 'function_call' && (item?.name as string) === 'end_interview') {
            setPhase('closing');
            setCurrentAiText('');
          }
          break;
        }

        // Detect end_interview function call — SDK handles function_call_output automatically
        case 'response.output_item.done': {
          const item = msg.item as Record<string, unknown> | null;
          if (item?.type === 'function_call' && item?.name === 'end_interview') {
            // Cancel any running silence poller so it doesn't overwrite 'idle' with 'listening'
            if (silenceRafRef.current) { cancelAnimationFrame(silenceRafRef.current); silenceRafRef.current = 0; }
            if (micRenableTimerRef.current) { clearTimeout(micRenableTimerRef.current); micRenableTimerRef.current = null; }
            // Flush so the closing message is persisted before recording stops.
            flushPendingAiEntries();
            setEnded(true);
            setOrbState('idle');
            stopRecorder(userRecorderRef.current);
            stopRecorder(aiRecorderRef.current);
            // Stop microphone tracks
            micStreamRef.current?.getTracks().forEach((t) => t.stop());
          }
          break;
        }

        // User speech transcription — arrives after the AI has often already started
        // responding. We replace the placeholder inserted at speech_started in-place
        // so the user turn stays before the AI response in the list.
        case 'conversation.item.input_audio_transcription.completed': {
          const itemId = msg.item_id as string;
          const text = (msg.transcript as string | undefined)?.trim();
          const transcribedAt = new Date().toISOString();
          const pendingId =
            pendingUserPlaceholderByItemIdRef.current[itemId]
            ?? pendingUserPlaceholderQueueRef.current.shift()
            ?? null;
          if (pendingUserPlaceholderByItemIdRef.current[itemId]) {
            delete pendingUserPlaceholderByItemIdRef.current[itemId];
          }
          // Update transcript display
          {
            // Compute synchronously from the ref so transcriptRef is current before
            // persistTranscript() runs (see flushPendingAiEntries note).
            const cur = transcriptRef.current;
            if (!cur.find((e) => e.id === itemId && e.role === 'user')) {
              const placeholderIdx = pendingId
                ? cur.findIndex((e) => e.id === pendingId)
                : cur.findIndex((e) => e.role === 'user' && e.id.startsWith('user-pending-'));
              const placeholder = placeholderIdx >= 0 ? cur[placeholderIdx] : null;
              const spokenAt =
                placeholder?.spokenAt
                ?? placeholder?.timestamp
                ?? pendingUserSpeechStartTimesRef.current.shift()
                ?? transcribedAt;
              let next: TranscriptEntry[];
              if (!text) {
                // No usable transcription — remove placeholder
                if (placeholderIdx >= 0) {
                  next = [...cur];
                  next.splice(placeholderIdx, 1);
                } else {
                  next = cur;
                }
              } else if (placeholderIdx >= 0) {
                // Replace in-place — keeps the user entry before the AI entries that follow
                next = [...cur];
                next[placeholderIdx] = {
                  ...next[placeholderIdx],
                  id: itemId,
                  role: 'user',
                  text,
                  timestamp: spokenAt,
                  spokenAt,
                  transcribedAt,
                };
              } else {
                // Fallback: no placeholder found (e.g. speech started while AI was responding)
                next = [...cur, { id: itemId, role: 'user', text, timestamp: spokenAt, spokenAt, transcribedAt }];
              }
              if (next !== cur) {
                transcriptRef.current = next;
                setTranscript(next);
              }
            }
            if (text) persistTranscript();
          }
          break;
        }

        case 'error': {
          const errMsg =
            (msg.error as Record<string, unknown> | null)?.message as string | undefined
            ?? 'Unknown error from realtime API';
          setError(errMsg);
          setStatus('error');
          break;
        }
      }
    },
    [setMicEnabled, armSilencePoller, flushPendingAiEntries, persistTranscript],
  );

  // ── start() ──────────────────────────────────────────────────────────────────

  const start = useCallback(
    async (
      voice = 'marin',
      existingStream?: MediaStream,
      noiseReductionType: 'near_field' | 'far_field' = 'far_field',
      outputDeviceId?: string,
    ) => {
      setStatus('connecting');
      setError(null);
      setEnded(false);
      setPhase('opening');
      setTranscript([]);
      transcriptRef.current = [];
      setCurrentAiText('');
      setAiTurnId('');
      partialRef.current = {};
      pendingUserPlaceholderQueueRef.current = [];
      pendingUserPlaceholderByItemIdRef.current = {};
      pendingUserSpeechStartTimesRef.current = [];
      pendingAiEntriesRef.current = [];
      {
        const now = new Date();
        const p = (n: number) => String(n).padStart(2, '0');
        const datePart = `${now.getFullYear()}${p(now.getMonth() + 1)}${p(now.getDate())}`;
        const timePart = `${p(now.getHours())}${p(now.getMinutes())}${p(now.getSeconds())}`;
        sessionIdRef.current = `demo-${datePart}-${timePart}-${Math.random().toString(36).slice(2, 7)}`;
      }
      userRecorderRef.current = null;
      aiRecorderRef.current = null;
      if (speechTimerRef.current) { clearTimeout(speechTimerRef.current); speechTimerRef.current = null; }
      userSpeakingRef.current = false;
      phaseRef.current = 'opening';

      try {
        // 1. Get ephemeral key from server
        const sessResp = await fetch('/api/realtime/session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ voice }),
        });
        if (!sessResp.ok) {
          const e = await sessResp.json().catch(() => ({ error: sessResp.statusText }));
          throw new Error((e as Record<string, string>).error ?? 'Failed to create session');
        }
        const sessData = await sessResp.json();
        const ephemeralKey = (sessData as Record<string, string>).value;
        if (!ephemeralKey) throw new Error('No ephemeral key in session response');

        // 2. Mic stream — reuse the one acquired during the audio check if provided,
        //    otherwise prompt and acquire with echo cancellation.
        let stream: MediaStream;
        if (existingStream) {
          stream = existingStream;
        } else {
          try {
            stream = await navigator.mediaDevices.getUserMedia({
              audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
            });
          } catch (micErr) {
            const name = micErr instanceof Error ? micErr.name : '';
            throw new Error(
              name === 'NotAllowedError'
                ? 'Microphone access was denied. Please allow microphone access and try again.'
                : 'Could not access microphone.',
            );
          }
        }
        micStreamRef.current = stream;
        userRecorderRef.current = createStreamingRecorder(stream, (blob) => sendAudioChunk('userAudioChunk', blob));

        // Briefly mute on mousedown to prevent click sounds from triggering VAD.
        // Skipped if user manually muted or AI is currently responding.
        const handleMouseDown = () => {
          if (userMutedRef.current || isAiRespondingRef.current) return;
          setMicEnabled(false);
          setTimeout(() => {
            if (!userMutedRef.current && !isAiRespondingRef.current) {
              setMicEnabled(true);
            }
          }, 150);
        };
        mouseDownHandlerRef.current = handleMouseDown;
        document.addEventListener('mousedown', handleMouseDown);

        // 3. Mic level polling for orb animation
        const micCtx = new AudioContext();
        micCtxRef.current = micCtx;
        const micAnalyser = micCtx.createAnalyser();
        micAnalyser.fftSize = 256;
        micCtx.createMediaStreamSource(stream).connect(micAnalyser);
        const levelBuf = new Uint8Array(micAnalyser.frequencyBinCount);
        const pollLevel = () => {
          micAnalyser.getByteFrequencyData(levelBuf);
          const avg = levelBuf.reduce((s, v) => s + v, 0) / levelBuf.length / 128;
          setMicLevel(Math.min(avg * 2.5, 1));
          rafRef.current = requestAnimationFrame(pollLevel);
        };
        rafRef.current = requestAnimationFrame(pollLevel);

        // 4. Audio element for remote AI playback — route to selected speaker if any
        const audioEl = new Audio();
        audioEl.autoplay = true;
        if (outputDeviceId && 'setSinkId' in audioEl) {
          (audioEl as HTMLAudioElement & { setSinkId(id: string): Promise<void> })
            .setSinkId(outputDeviceId).catch(console.error);
        }

        // 4b. Remote AudioContext for AnalyserNode silence detection.
        //     Created inside a user-gesture handler so the browser won't auto-suspend it.
        const remoteCtx = new AudioContext();
        remoteAudioCtxRef.current = remoteCtx;
        await remoteCtx.resume();

        // 5. WebRTC transport — changePeerConnection wires the AnalyserNode to the
        //    remote audio track once the peer connection receives it.
        //    createMediaStreamSource on the track's stream (not createMediaElementSource)
        //    is required: createMediaElementSource silently produces no data when the
        //    audio element's srcObject is a live WebRTC MediaStream in Chrome.
        //    The audio element continues playing normally via its own srcObject pipeline;
        //    the MediaStreamSource only taps it for analysis and does not need to connect
        //    to ctx.destination.
        const transport = new OpenAIRealtimeWebRTC({
          mediaStream: stream,
          audioElement: audioEl,
          changePeerConnection: async (pc: RTCPeerConnection) => {
            pc.addEventListener('track', (ev: RTCTrackEvent) => {
              const ctx = remoteAudioCtxRef.current;
              if (!ctx || remoteAnalyserRef.current) return;
              ctx.resume().catch(() => {});
              const analyser = ctx.createAnalyser();
              analyser.fftSize = 256;
              const remoteStream = new MediaStream([ev.track]);
              const source = ctx.createMediaStreamSource(remoteStream);
              source.connect(analyser);
              remoteAnalyserRef.current = analyser;
              aiRecorderRef.current = createStreamingRecorder(remoteStream, (blob) => sendAudioChunk('aiAudioChunk', blob));

              // Poll AI speaker level for orb animation
              const aiLevelBuf = new Uint8Array(analyser.frequencyBinCount);
              const pollAiLevel = () => {
                analyser.getByteFrequencyData(aiLevelBuf);
                const avg = aiLevelBuf.reduce((s, v) => s + v, 0) / aiLevelBuf.length / 128;
                setAiLevel(Math.min(avg * 2.5, 1));
                aiLevelRafRef.current = requestAnimationFrame(pollAiLevel);
              };
              aiLevelRafRef.current = requestAnimationFrame(pollAiLevel);
            });
            return pc;
          },
        });

        // 6. Create session — SDK sends session.update with full config on connect
        const realtimeSession = new RealtimeSession(interviewAgent, {
          model: 'gpt-realtime-2',
          transport,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          config: {
            audio: {
              input: {
                transcription: { model: 'whisper-1', language: 'en' },
                turnDetection: {
                  type: 'semantic_vad',
                  interruptResponse: false,
                  createResponse: false,
                  eagerness: 'high',
                },
                noiseReduction: { type: noiseReductionType },
              },
              output: { voice },
            },
          } as Partial<RealtimeSessionConfig>,
        });
        sessionRef.current = realtimeSession;

        // 7. Wire raw event handler before connecting
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (realtimeSession.transport as any).on('*', handleRawEvent);

        // 8. Connect — resolves after session.updated is acknowledged by server
        await realtimeSession.connect({ apiKey: ephemeralKey });

        // 8b. GUARANTEE interrupt_response:false before the AI's first audio (the
        //     server DEFAULTS it to true). Belt-and-suspenders even with the muted
        //     design — the mic re-opens for capture near the message end, and we never
        //     want a late reply there to truncate the AI. We push the turn-detection
        //     config explicitly, raw (snake_case) — the same shape the
        //     opening→interviewing transition uses and that the server honors — rather
        //     than relying solely on the SDK's camelCase initial config.
        realtimeSession.transport.sendEvent({
          type: 'session.update',
          session: { type: 'realtime', audio: { input: { turn_detection: OPENING_VAD_CONFIG } } },
        });

        // 9. Session is ready; trigger the AI's opening message
        setStatus('ready');
        setOrbState('thinking');
        realtimeSession.transport.sendEvent({ type: 'response.create' });
        isAiRespondingRef.current = true; // guard the gap until response.created
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        setStatus('error');
        setOrbState('idle');
        stopResources();
      }
    },
    [handleRawEvent, stopResources, setMicEnabled, sendAudioChunk],
  );

  // ── stop() ───────────────────────────────────────────────────────────────────

  const stop = useCallback(() => {
    persistTranscript();
    stopRecorder(userRecorderRef.current);
    stopRecorder(aiRecorderRef.current);
    userRecorderRef.current = null;
    aiRecorderRef.current = null;
    const session = sessionRef.current;
    sessionRef.current = null;
    session?.close();
    stopResources();
    partialRef.current = {};
    pendingUserPlaceholderQueueRef.current = [];
    pendingUserPlaceholderByItemIdRef.current = {};
    pendingUserSpeechStartTimesRef.current = [];
    pendingAiEntriesRef.current = [];
    if (speechTimerRef.current) { clearTimeout(speechTimerRef.current); speechTimerRef.current = null; }
    userSpeakingRef.current = false;
    phaseRef.current = 'opening';
    setStatus('idle');
    setOrbState('idle');
    setEnded(false);
    setPhase('opening');
    setError(null);
    setTranscript([]);
    transcriptRef.current = [];
    setCurrentAiText('');
    setAiTurnId('');
  }, [stopResources, persistTranscript]);

  // ── Actions ──────────────────────────────────────────────────────────────────

  const sendText = useCallback((text: string) => {
    const session = sessionRef.current;
    if (!session) return;

    // If AI audio is still playing, stop the silence poller and flush what was said
    if (isAiRespondingRef.current) {
      // Only cancel the API response if it's still active — after response.done
      // the response is closed and cancelling would return an error
      if (isResponseActiveRef.current) {
        session.transport.sendEvent({ type: 'response.cancel' });
        isResponseActiveRef.current = false;
      }
      if (silenceRafRef.current) { cancelAnimationFrame(silenceRafRef.current); silenceRafRef.current = 0; }
      if (micRenableTimerRef.current) { clearTimeout(micRenableTimerRef.current); micRenableTimerRef.current = null; }
      isAiRespondingRef.current = false;
      flushPendingAiEntries();
    }

    const id = `txt-${Date.now()}`;
    if (phaseRef.current === 'opening') {
      phaseRef.current = 'interviewing';
      setPhase('interviewing');
      session.transport.sendEvent({
        type: 'session.update',
        session: { type: 'realtime', audio: { input: { turn_detection: INTERVIEWING_VAD_CONFIG } } },
      });
    }
    const nextWithText: TranscriptEntry[] = [...transcriptRef.current, { id, role: 'user', text, timestamp: new Date().toISOString() }];
    transcriptRef.current = nextWithText;
    setTranscript(nextWithText);
    persistTranscript();
    session.transport.sendEvent({
      type: 'conversation.item.create',
      item: { type: 'message', role: 'user', content: [{ type: 'input_text', text }] },
    });
    session.transport.sendEvent({ type: 'response.create' });
    isAiRespondingRef.current = true; // guard the gap until response.created
    setOrbState('thinking');
  }, [flushPendingAiEntries, persistTranscript]);

  const repeat = useCallback(() => {
    const session = sessionRef.current;
    if (!session) return;
    session.transport.sendEvent({ type: 'input_audio_buffer.clear' });
    session.transport.sendEvent({
      type: 'conversation.item.create',
      item: {
        type: 'message',
        role: 'user',
        content: [{ type: 'input_text', text: 'Could you please repeat your last question?' }],
      },
    });
    session.transport.sendEvent({ type: 'response.create' });
    isAiRespondingRef.current = true; // guard the gap until response.created
    setOrbState('thinking');
  }, []);

  const mute = useCallback(
    (shouldMute: boolean) => {
      userMutedRef.current = shouldMute;
      setMicEnabled(!shouldMute);
    },
    [setMicEnabled],
  );

  const pause = useCallback((shouldPause: boolean) => {
    sessionRef.current?.transport.sendEvent({
      type: 'session.update',
      session: {
        type: 'realtime',
        audio: { input: { turn_detection: shouldPause ? null : (phaseRef.current === 'opening' ? OPENING_VAD_CONFIG : INTERVIEWING_VAD_CONFIG) } },
      },
    });
  }, []);

  return {
    status,
    orbState,
    micLevel,
    aiLevel,
    ended,
    phase,
    error,
    transcript,
    currentAiText,
    streamingAiText,
    aiTurnId,
    start,
    stop,
    sendText,
    repeat,
    mute,
    pause,
  };
}
